import React from 'react';
import { Stack, Dropdown, TextField, PrimaryButton, Text, MessageBar, MessageBarType, getTheme } from '@fluentui/react';
import { quoteCategories } from './QuoteAPI';
import { useEditedWidget } from 'features/Rooms/PersonalMeetingCorner/states/hooks';
import IvicosStrings from 'kits/language/stringKit';

interface QuoteViewProps {
    randomQuote: string;
    randomAuthor: string;
    customQuote: string;
    setCustomQuote: (quote: string) => void;
    customAuthor: string;
    setCustomAuthor: (author: string) => void;
    quoteCategory: string;
    setQuoteCategory: (category: string) => void;
    isGenerating: boolean;
    error: string;
    setError: (error: string) => void;
    generateQuotes: () => Promise<void>;
    useRandomQuote: () => void;
}

export const QuoteView: React.FC<QuoteViewProps> = ({
    randomQuote,
    randomAuthor,
    customQuote,
    setCustomQuote,
    customAuthor,
    setCustomAuthor,
    quoteCategory,
    setQuoteCategory,
    isGenerating,
    error,
    setError,
    generateQuotes,
    useRandomQuote
}) => {
    const theme = getTheme();
    const [editedWidget] = useEditedWidget();
    const isEditMode = editedWidget?.content?.quote && editedWidget?.content?.author;

    return (
        <Stack tokens={{ childrenGap: 24 }}>
            {/* Random Quote Box */}
            {!isEditMode && (
                <Stack
                    tokens={{ childrenGap: 12 }}
                    styles={{
                        root: {
                            background: theme.palette.neutralLighter,
                            border: `1px solid ${theme.palette.neutralLight}`,
                            borderRadius: 8,
                            padding: 20,
                            boxShadow: theme.effects.elevation8,
                            width: '100%',
                            margin: '0 auto'
                        }
                    }}
                >
                    <Stack horizontal tokens={{ childrenGap: 12 }} verticalAlign="end">
                        <Dropdown
                            placeholder={IvicosStrings.personalMeetingCornerStrings.quote.chooseCategory}
                            selectedKey={quoteCategory}
                            options={quoteCategories}
                            onChange={(_, option) => option && setQuoteCategory(option.key as string)}
                            styles={{ root: { minWidth: 180, borderRadius: '10px' } }}
                        />
                        <PrimaryButton
                            text={
                                isGenerating
                                    ? IvicosStrings.personalMeetingCornerStrings.quote.fetchingQuote
                                    : IvicosStrings.personalMeetingCornerStrings.quote.getQuote
                            }
                            onClick={generateQuotes}
                            disabled={isGenerating}
                            iconProps={isGenerating ? { iconName: 'Spinner' } : undefined}
                        />
                    </Stack>
                    <Stack styles={{ root: { background: '#fff', borderRadius: 8, padding: 16, border: `1px solid ${theme.palette.neutralQuaternaryAlt}` } }}>
                        {randomQuote ? (
                            <>
                                <Text variant="large" styles={{ root: { fontStyle: 'italic', color: theme.palette.neutralPrimary } }}>
                                    &ldquo;{randomQuote}&rdquo;
                                </Text>
                                <Text variant="medium" styles={{ root: { color: theme.palette.neutralSecondary, marginTop: 8 } }}>
                                    — {randomAuthor}
                                </Text>
                            </>
                        ) : (
                            <Text variant="medium" styles={{ root: { color: theme.palette.neutralTertiary } }}>
                                {IvicosStrings.personalMeetingCornerStrings.quote.noQuoteGenerated}
                            </Text>
                        )}
                        <PrimaryButton
                            text={IvicosStrings.personalMeetingCornerStrings.quote.useThisQuote}
                            onClick={useRandomQuote}
                            disabled={!randomQuote}
                            styles={{ root: { marginTop: 16, alignSelf: 'flex-end' } }}
                        />
                    </Stack>
                </Stack>
            )}

            {error && (
                <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setError('')}>
                    {error}
                </MessageBar>
            )}

            {/* Manual Fields */}
            <Stack tokens={{ childrenGap: 12 }} styles={{ root: { marginBottom: 12 } }}>
                <TextField
                    label={IvicosStrings.personalMeetingCornerStrings.quote.quoteLabel}
                    placeholder={IvicosStrings.personalMeetingCornerStrings.quote.quotePlaceholder}
                    multiline
                    rows={3}
                    value={customQuote}
                    onChange={(_, newValue) => setCustomQuote(newValue || '')}
                    maxLength={400}
                    required
                    errorMessage={!customQuote.trim() && customAuthor.trim() ? 'Quote is required' : undefined}
                />
                <TextField
                    label={IvicosStrings.personalMeetingCornerStrings.quote.authorLabel}
                    placeholder={IvicosStrings.personalMeetingCornerStrings.quote.authorPlaceholder}
                    value={customAuthor}
                    onChange={(_, newValue) => setCustomAuthor(newValue || '')}
                    required
                    errorMessage={!customAuthor.trim() && customQuote.trim() ? 'Author is required' : undefined}
                />
            </Stack>
        </Stack>
    );
};
